/**
 * Page Détail Produit - Design Amazon
 * E-Store - FrontOffice
 */

import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  useAuth,
  useCart,
  catalogApi,
  reviewApi,
  ArticleWithStock,
  SuggestionGroup,
  enterpriseApi,
  EnterprisePublicInfo,
  StarRating,
  RatingDistribution,
  Review,
  ArticleReviewStats,
  CreateReviewInput,
  SearchableSelect,
  getBrandInfo,
  hasStock,
  isStockless,
  isDeliverable,
  ARTICLE_TYPE_LABELS,
} from '@e-store/shared';
import { AmazonHeader } from '../components/AmazonHeader';
import './ProductDetailPage.css';

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { addToCart } = useCart();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [article, setArticle] = useState<ArticleWithStock | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [suggestionGroups, setSuggestionGroups] = useState<SuggestionGroup[]>([]);
  const [enterprise, setEnterprise] = useState<EnterprisePublicInfo | null>(null);

  // Reviews state
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewStats, setReviewStats] = useState<ArticleReviewStats | null>(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState<CreateReviewInput>({
    articleId: '',
    rating: 5,
    title: '',
    comment: '',
  });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [sellerReplyText, setSellerReplyText] = useState('');
  const [replyingToReviewId, setReplyingToReviewId] = useState<string | null>(null);

  // Check if user is BackOffice (can reply to reviews)
  const isBackOfficeUser = user?.portal === 'backoffice';

  useEffect(() => {
    loadEnterpriseInfo();
    if (id) {
      loadArticle();
      loadSuggestions();
      loadReviews();
      loadReviewStats();
    }
  }, [id]);

  // Scroll to reviews section if hash is #reviews
  useEffect(() => {
    if (location.hash === '#reviews' && !loading) {
      setTimeout(() => {
        const reviewsSection = document.getElementById('reviews');
        if (reviewsSection) {
          reviewsSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location.hash, loading]);

  const loadEnterpriseInfo = async () => {
    try {
      const response = await enterpriseApi.getPublicInfo();
      if (response.data) {
        setEnterprise(response.data);
      }
    } catch (err) {
      console.error('Erreur chargement infos entreprise:', err);
    }
  };

  const loadArticle = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError('');
      const response = await catalogApi.getProduct(id);

      if (response.error) {
        setError(response.error);
        return;
      }

      if (response.data) {
        setArticle(response.data);
        setSelectedImageIndex(response.data.mainImageIndex || 0);
      }
    } catch (err: any) {
      setError('Erreur lors du chargement du produit');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestions = async () => {
    if (!id) return;

    try {
      const response = await catalogApi.getProductSuggestions(id);

      if (response.error) {
        console.error('Erreur chargement suggestions:', response.error);
        return;
      }

      setSuggestionGroups(response.data || []);
    } catch (err) {
      console.error('Erreur chargement suggestions:', err);
    }
  };

  const loadReviews = async () => {
    if (!id) return;

    try {
      setReviewsLoading(true);
      const response = await reviewApi.getArticleReviews(id, { pageSize: 20 });
      if (response.data) {
        setReviews(response.data.reviews || []);
      }
    } catch (err) {
      console.error('Erreur chargement avis:', err);
    } finally {
      setReviewsLoading(false);
    }
  };

  const loadReviewStats = async () => {
    if (!id) return;

    try {
      const response = await reviewApi.getArticleReviewStats(id);
      if (response.data) {
        setReviewStats(response.data);
      }
    } catch (err) {
      console.error('Erreur chargement stats avis:', err);
    }
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !isAuthenticated) return;

    try {
      setSubmittingReview(true);
      const response = await reviewApi.createReview({
        ...newReview,
        articleId: id,
      });

      if (response.error) {
        setError(response.error);
        return;
      }

      setSuccess('Votre avis a ete soumis et sera publie apres moderation');
      setShowReviewForm(false);
      setNewReview({ articleId: '', rating: 5, title: '', comment: '' });
      // Reload reviews
      loadReviews();
      loadReviewStats();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la soumission');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleSellerReply = async (reviewId: string) => {
    if (!sellerReplyText.trim()) return;

    try {
      const response = await reviewApi.addSellerReply(reviewId, sellerReplyText);
      if (response.error) {
        setError(response.error);
        return;
      }

      setSuccess('Reponse ajoutee');
      setReplyingToReviewId(null);
      setSellerReplyText('');
      loadReviews();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la reponse');
    }
  };

  const handleVote = async (reviewId: string, isHelpful: boolean) => {
    try {
      await reviewApi.voteOnReview(reviewId, isHelpful);
      loadReviews();
    } catch (err) {
      console.error('Erreur vote:', err);
    }
  };

  const formatReviewDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const handleAddToCart = async () => {
    if (!article) return;

    const result = await addToCart({
      articleId: article.id,
      quantity,
    });

    if (result.success) {
      setSuccess(`${article.name} ajouté au panier`);
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Erreur lors de l\'ajout au panier');
    }
  };

  const handleQuickAddToCart = async (suggestedArticle: ArticleWithStock) => {
    const result = await addToCart({
      articleId: suggestedArticle.id,
      quantity: 1,
    });

    if (result.success) {
      setSuccess(`${suggestedArticle.name} ajouté au panier`);
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Erreur lors de l\'ajout au panier');
    }
  };

  const formatPrice = (price: number) => {
    const currency = enterprise?.currency || 'MGA';
    const symbol = enterprise?.currencySymbol || 'Ar';

    if (currency === 'EUR') {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
      }).format(price);
    }

    return `${new Intl.NumberFormat('fr-FR').format(price)} ${symbol}`;
  };

  const images = article?.images || [];
  const currentImage = images[selectedImageIndex] || null;

  if (loading) {
    return (
      <div className="product-detail-page">
        <AmazonHeader />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement du produit...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="product-detail-page">
        <AmazonHeader />
        <div className="error-container">
          <div className="error-icon">😕</div>
          <h2>Produit introuvable</h2>
          <p>{error || 'Ce produit n\'existe pas ou n\'est plus disponible.'}</p>
          <Link to="/catalog" className="btn-back-catalog">
            ← Retour au catalogue
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <AmazonHeader />

      {/* Breadcrumb */}
      <div className="breadcrumb-container">
        <nav className="breadcrumb">
          <Link to="/">Accueil</Link>
          <span className="separator">›</span>
          <Link to="/catalog">Catalogue</Link>
          <span className="separator">›</span>
          <span className="current">{article.name}</span>
        </nav>
      </div>

      {/* Messages */}
      {success && (
        <div className="toast-message success">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          <span>{success}</span>
          <button onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Product Content */}
      <div className="product-content">
        <div className="product-main">
          {/* Images Section */}
          <div className="product-images">
            {/* Thumbnails - Left Side */}
            {images.length > 1 && (
              <div className="thumbnails-vertical">
                {images.map((img, index) => (
                  <button
                    key={index}
                    className={`thumbnail ${index === selectedImageIndex ? 'active' : ''}`}
                    onMouseEnter={() => setSelectedImageIndex(index)}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img src={img} alt={`${article.name} ${index + 1}`} />
                  </button>
                ))}
              </div>
            )}

            {/* Main Image */}
            <div className="main-image-wrapper">
              <div className="main-image">
                {currentImage ? (
                  <img src={currentImage} alt={article.name} />
                ) : (
                  <div className="no-image">
                    <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                    <span>Pas d'image</span>
                  </div>
                )}
              </div>
              {images.length > 1 && (
                <div className="image-counter">
                  {selectedImageIndex + 1} / {images.length}
                </div>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div className="product-info">
            <h1 className="product-title">{article.name}</h1>

            <div className="product-meta">
              <span className="reference">Réf: {article.reference}</span>
              {article.category && (
                <>
                  <span className="meta-separator">|</span>
                  <Link to={`/catalog?category=${article.categoryIds?.[0]}`} className="category-link">
                    {article.category}
                  </Link>
                </>
              )}
            </div>

            <div className="price-section">
              <div className="price-row">
                <span className="price-label">Prix:</span>
                <span className="price-value">{formatPrice(article.priceHT)}</span>
              </div>
              <div className="price-details">
                <span className="tva-info">TVA {article.tvaRate}% incluse</span>
              </div>
            </div>

            {/* Stock Status */}
            <div className="stock-section">
              {hasStock(article.type) ? (
                article.isInStock || (article.stockTotal && article.stockTotal > 0) ? (
                  <div className="stock-available">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span>En stock</span>
                    {article.stockTotal && article.stockTotal <= 5 && (
                      <span className="low-stock-warning">
                        Plus que {article.stockTotal} en stock - commandez vite !
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="stock-unavailable">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                    <span>Rupture de stock</span>
                  </div>
                )
              ) : (
                /* Types sans stock (service, restaurant) : toujours disponibles */
                <div className="stock-service">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                  <span>{ARTICLE_TYPE_LABELS[article.type]} disponible</span>
                </div>
              )}
            </div>

            {/* Description */}
            {article.description && (
              <div className="description-section">
                <h3>À propos de cet article</h3>
                <p>{article.description}</p>
              </div>
            )}
          </div>

          {/* Buy Box - Right Side */}
          <div className="buy-box">
            <div className="buy-box-price">
              <span className="price-value">{formatPrice(article.priceHT)}</span>
            </div>

            {/* Un plat est livrable mais stockless : sans le `isStockless`, le test de stock
                serait toujours faux et le badge n'apparaitrait jamais. Un produit en rupture
                reste sans badge (comportement actuel preserve). */}
            {isDeliverable(article.type) &&
              (isStockless(article.type) || article.isInStock || (article.stockTotal ?? 0) > 0) && (
              <div className="delivery-info">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="1" y="3" width="15" height="13"/>
                  <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                  <circle cx="5.5" cy="18.5" r="2.5"/>
                  <circle cx="18.5" cy="18.5" r="2.5"/>
                </svg>
                <span>Livraison disponible</span>
              </div>
            )}

            {/* Classe `service` réutilisée pour tous les types sans stock (CSS existante) */}
            <div className={`stock-status ${
              isStockless(article.type)
                ? 'service'
                : (article.isInStock || (article.stockTotal && article.stockTotal > 0))
                  ? 'in-stock'
                  : 'out-of-stock'
            }`}>
              {isStockless(article.type)
                ? `${ARTICLE_TYPE_LABELS[article.type]} disponible`
                : (article.isInStock || (article.stockTotal && article.stockTotal > 0))
                  ? 'En stock'
                  : 'Rupture de stock'
              }
            </div>

            {/* Quantity Selector */}
            {(isStockless(article.type) || article.isInStock || (article.stockTotal && article.stockTotal > 0)) && (
              <>
                <div className="quantity-section">
                  <label>Quantité:</label>
                  <SearchableSelect
                    value={String(quantity)}
                    onChange={(val) => setQuantity(parseInt(val))}
                    options={Array.from(
                      { length: isStockless(article.type) ? 10 : Math.min(article.stockTotal || 10, 10) },
                      (_, i) => ({ value: String(i + 1), label: String(i + 1) })
                    )}
                    className="quantity-select"
                    required
                  />
                  {hasStock(article.type) && article.stockTotal && article.stockTotal < 10 && (
                    <span className="stock-limit-hint">
                      (max {article.stockTotal} disponible)
                    </span>
                  )}
                </div>

                <button
                  className="btn-add-to-cart"
                  onClick={handleAddToCart}
                  disabled={hasStock(article.type) && (!article.stockTotal || article.stockTotal < quantity)}
                  title={hasStock(article.type) && article.stockTotal && article.stockTotal < quantity
                    ? `Stock insuffisant (${article.stockTotal} disponible)`
                    : ''}
                >
                  Ajouter au panier
                </button>

                <button
                  className="btn-buy-now"
                  onClick={() => {
                    handleAddToCart();
                    navigate('/cart');
                  }}
                  disabled={hasStock(article.type) && (!article.stockTotal || article.stockTotal < quantity)}
                >
                  Acheter maintenant
                </button>
              </>
            )}

            {/* Secure Transaction */}
            <div className="secure-info">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <span>Transaction sécurisée</span>
            </div>

            {/* Seller Info */}
            <div className="seller-info">
              <div className="seller-row">
                <span className="seller-label">Vendu par:</span>
                <span className="seller-value">{enterprise?.name || getBrandInfo().name}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Suggestions Section */}
        {suggestionGroups.length > 0 && (
          <div className="suggestions-section">
            {suggestionGroups.map((group) => (
              <div key={group.group.id} className="suggestion-group">
                <h2 className="suggestion-title">
                  {group.group.name || 'Produits complémentaires'}
                </h2>
                {group.group.description && (
                  <p className="suggestion-description">{group.group.description}</p>
                )}

                <div className="suggestions-carousel">
                  {group.articles.map((suggestedArticle) => (
                    <div key={suggestedArticle.id} className="suggestion-card">
                      <Link to={`/product/${suggestedArticle.id}`} className="suggestion-image">
                        {suggestedArticle.images && suggestedArticle.images.length > 0 ? (
                          <img
                            src={suggestedArticle.images[suggestedArticle.mainImageIndex || 0]}
                            alt={suggestedArticle.name}
                          />
                        ) : (
                          <div className="no-image-small">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                              <circle cx="8.5" cy="8.5" r="1.5"/>
                              <polyline points="21 15 16 10 5 21"/>
                            </svg>
                          </div>
                        )}
                      </Link>

                      <div className="suggestion-info">
                        <Link to={`/product/${suggestedArticle.id}`} className="suggestion-name">
                          {suggestedArticle.name}
                        </Link>

                        {isStockless(suggestedArticle.type) ? (
                          <span className="badge-service">{ARTICLE_TYPE_LABELS[suggestedArticle.type]}</span>
                        ) : (
                          <span className={`suggestion-stock ${suggestedArticle.isInStock ? 'in-stock' : 'out-of-stock'}`}>
                            {suggestedArticle.isInStock ? 'En stock' : 'Rupture'}
                          </span>
                        )}

                        <div className="suggestion-price">{formatPrice(suggestedArticle.priceHT)}</div>

                        <button
                          className="btn-suggestion-add"
                          onClick={() => handleQuickAddToCart(suggestedArticle)}
                          disabled={hasStock(suggestedArticle.type) && !suggestedArticle.isInStock}
                        >
                          Ajouter
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Reviews Section */}
        <div id="reviews" className="reviews-section">
          <div className="reviews-header">
            <h2>Avis clients</h2>

            {/* Reviews Summary */}
            <div className="reviews-summary">
              <div className="rating-overview">
                {reviewStats ? (
                  <>
                    <div className="average-rating">
                      <span className="rating-number">{reviewStats.averageRating.toFixed(1)}</span>
                      <span className="rating-max">/ 5</span>
                    </div>
                    <StarRating
                      rating={reviewStats.averageRating}
                      totalReviews={reviewStats.totalReviews}
                      size="large"
                      showCount={true}
                    />
                    <p className="total-reviews">{reviewStats.totalReviews} avis client{reviewStats.totalReviews !== 1 ? 's' : ''}</p>
                  </>
                ) : (
                  <p className="no-reviews-yet">Aucun avis pour le moment</p>
                )}
              </div>

              {/* Rating Distribution */}
              {reviewStats && reviewStats.totalReviews > 0 && (
                <div className="rating-distribution">
                  <RatingDistribution
                    distribution={reviewStats.ratingDistribution}
                    total={reviewStats.totalReviews}
                  />
                </div>
              )}
            </div>

            {/* Write Review Button */}
            {isAuthenticated && !isBackOfficeUser && (
              <button
                className="btn-write-review"
                onClick={() => setShowReviewForm(!showReviewForm)}
              >
                Donner votre avis
              </button>
            )}

            {!isAuthenticated && (
              <Link to="/login" className="btn-write-review-login">
                Connectez-vous pour donner votre avis
              </Link>
            )}
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <form className="review-form" onSubmit={handleSubmitReview}>
              <h3>Votre avis sur ce produit</h3>

              <div className="form-group">
                <label>Votre note</label>
                <div className="rating-input">
                  <StarRating
                    rating={newReview.rating}
                    interactive={true}
                    onRatingChange={(rating) => setNewReview({ ...newReview, rating })}
                    size="large"
                    showCount={false}
                  />
                  <span className="rating-text">
                    {newReview.rating === 5 && 'Excellent'}
                    {newReview.rating === 4 && 'Tres bien'}
                    {newReview.rating === 3 && 'Bien'}
                    {newReview.rating === 2 && 'Moyen'}
                    {newReview.rating === 1 && 'Mauvais'}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label>Titre de votre avis</label>
                <input
                  type="text"
                  value={newReview.title}
                  onChange={(e) => setNewReview({ ...newReview, title: e.target.value })}
                  placeholder="Resumez votre avis en quelques mots"
                  required
                  minLength={3}
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <label>Votre commentaire</label>
                <textarea
                  value={newReview.comment}
                  onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                  placeholder="Partagez votre experience avec ce produit..."
                  required
                  minLength={10}
                  maxLength={2000}
                  rows={5}
                />
                <span className="char-count">{newReview.comment.length}/2000</span>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowReviewForm(false)}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-submit-review"
                  disabled={submittingReview}
                >
                  {submittingReview ? 'Envoi...' : 'Soumettre mon avis'}
                </button>
              </div>
            </form>
          )}

          {/* Reviews List */}
          <div className="reviews-list">
            {reviewsLoading ? (
              <div className="reviews-loading">
                <div className="loading-spinner"></div>
                <p>Chargement des avis...</p>
              </div>
            ) : reviews.length === 0 ? (
              <div className="no-reviews">
                <p>Aucun avis pour ce produit.</p>
                <p>Soyez le premier a donner votre avis !</p>
              </div>
            ) : (
              reviews.map((review) => (
                <div key={review.id} className="review-card">
                  <div className="review-header">
                    <div className="reviewer-info">
                      <span className="reviewer-avatar">
                        {review.userName.charAt(0).toUpperCase()}
                      </span>
                      <span className="reviewer-name">{review.userName}</span>
                      {review.isVerifiedPurchase && (
                        <span className="verified-badge">Achat verifie</span>
                      )}
                    </div>
                    <span className="review-date">{formatReviewDate(review.createdAt)}</span>
                  </div>

                  <div className="review-rating">
                    <StarRating rating={review.rating} showCount={false} size="small" />
                    <span className="review-title">{review.title}</span>
                  </div>

                  <p className="review-comment">{review.comment}</p>

                  {/* Seller Reply */}
                  {review.sellerReply && (
                    <div className="seller-reply">
                      <div className="seller-reply-header">
                        <span className="seller-badge">Reponse du vendeur</span>
                        <span className="seller-name">{review.sellerReplyByName}</span>
                        {review.sellerReplyAt && (
                          <span className="reply-date">{formatReviewDate(review.sellerReplyAt)}</span>
                        )}
                      </div>
                      <p className="seller-reply-text">{review.sellerReply}</p>
                    </div>
                  )}

                  {/* Reply Form for BackOffice users */}
                  {isBackOfficeUser && !review.sellerReply && (
                    <div className="seller-reply-section">
                      {replyingToReviewId === review.id ? (
                        <div className="reply-form">
                          <textarea
                            value={sellerReplyText}
                            onChange={(e) => setSellerReplyText(e.target.value)}
                            placeholder="Votre reponse..."
                            rows={3}
                            minLength={10}
                            maxLength={1000}
                          />
                          <div className="reply-actions">
                            <button
                              className="btn-cancel-reply"
                              onClick={() => {
                                setReplyingToReviewId(null);
                                setSellerReplyText('');
                              }}
                            >
                              Annuler
                            </button>
                            <button
                              className="btn-send-reply"
                              onClick={() => handleSellerReply(review.id)}
                              disabled={sellerReplyText.length < 10}
                            >
                              Envoyer
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          className="btn-reply"
                          onClick={() => setReplyingToReviewId(review.id)}
                        >
                          Repondre
                        </button>
                      )}
                    </div>
                  )}

                  {/* Helpful Votes */}
                  <div className="review-footer">
                    <span className="helpful-text">Cet avis vous a-t-il ete utile ?</span>
                    <button
                      className="btn-helpful"
                      onClick={() => handleVote(review.id, true)}
                    >
                      Oui ({review.helpfulCount})
                    </button>
                    <button
                      className="btn-not-helpful"
                      onClick={() => handleVote(review.id, false)}
                    >
                      Non ({review.notHelpfulCount})
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="product-footer">
        <div className="footer-content">
          <p>© {new Date().getFullYear()} {enterprise?.name || getBrandInfo().name} - Tous droits reserves</p>
        </div>
      </footer>
    </div>
  );
}
